/**
 * Sam Assistant chat router — frontend entry point.
 *
 * Routes the React widget to BRANDCENTRAL's AIOS chat endpoint
 * (`POST https://app.brandctl.com/api/chat`). The system prompt lives on AIOS;
 * its spec is docs/AIOS_CHAT_AGENT_PORT.md. AIOS returns the exact
 * `ChatRouteOutput` shape the widget already sanitizes, and never 4xx/5xxs
 * a chat turn — errors come back as an in-shape reply.
 *
 * # Flow
 *
 * 1. ChatWidget calls `routeChat({ message, history, sessionId })`.
 * 2. `routeChat` truncates inputs (message ≤ 2000 chars, history capped to
 *    20 turns). An empty message is the widget's first-open "give me the
 *    opener" request — the stub answers it instantly, no model call.
 * 3. `callChatAgent()` POSTs to AIOS with a 30s timeout.
 * 4. `routeChat` sanitizes the response. On any failure (timeout, non-2xx,
 *    malformed output, network error) the on-device stub router takes over
 *    so the widget never shows an empty bubble.
 */

import type { ChatRouteInput, ChatRouteOutput } from './types';
import {
  sanitizeChatRouteOutput,
  stubRouteChat,
  withTimeout,
  HISTORY_CAP,
  CHAT_AGENT_TIMEOUT_MS,
} from './chatStub';

const AIOS_CHAT_ENDPOINT = 'https://app.brandctl.com/api/chat';

const callChatAgent = async (input: ChatRouteInput): Promise<unknown> => {
  const response = await fetch(AIOS_CHAT_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error(`chat HTTP ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const routeChat = async (
  input: ChatRouteInput,
): Promise<ChatRouteOutput> => {
  // Always keep the conversation tail (most recent context) and drop the
  // head if it would exceed the cap.
  const safeInput: ChatRouteInput = {
    message: input.message.slice(0, 2000),
    history: input.history.slice(-HISTORY_CAP),
    sessionId: input.sessionId,
  };

  // Empty message = the widget's first-open opener request. The stub has the
  // canonical greeting + service chips; no reason to spend a model call.
  if (!safeInput.message.trim()) {
    return stubRouteChat(safeInput);
  }

  try {
    const raw = await withTimeout(callChatAgent(safeInput), CHAT_AGENT_TIMEOUT_MS);
    const sanitized = sanitizeChatRouteOutput(raw);
    if (sanitized) return sanitized;
    // eslint-disable-next-line no-console
    console.warn('[chatRouter] AIOS chat returned malformed output; using stub');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[chatRouter] AIOS chat failed, falling back to stub:', err);
  }
  return stubRouteChat(safeInput);
};
